export interface Value {
  assets: AssetValue[],
}

export interface AssetValue {
  asset: string,
  value: bigint,
}

export interface Account {
  accountId: string,
  delegatedKey: string | null,
  uncommittedValue: Value,
  nonce: bigint,
}

export interface Deposit {
  accountId: string,
  requestId: bigint,
  absorbedInBlock: bigint,
  rejectedInBlock: bigint,
  refundTx: Uint8Array | null,
  failureReason: string | null,
  value: Value,
}

export interface Market {
  key: string,
  tradingPair: TradingPair,
  takerFee: Ratio,
  makerFee: Ratio,
}

export interface OrderbookDepth {
  key: string,
  pairKey: string,
  pair: TradingPair,
  price: bigint,
  bidAmount: bigint,
  askAmount: bigint,
}

export interface Order {
  id: bigint,
  owner: string,
  pair: TradingPair,
  side: Side,
  price: bigint,
  originalAmount: bigint,
  remaining: bigint,
  status: OrderStatus,
  created: bigint,
  destination: Destination,
  lastExecuted: bigint,
}

export interface Candlestick {
  key: string,
  pairKey: string,
  granularity: string,
  timestamp: bigint,
  open: bigint,
  high: bigint,
  low: bigint,
  close: bigint,
  volume: bigint,
  tradeCount: number,
}

export interface TpsStats {
  id: number,
  totalEvents: bigint,
  lastEventAt: bigint,
  currentSecondCount: number,
  currentSecond: bigint,
  lastSecondCount: number,
}

export interface TradingPair {
  base: AssetId,
  quote: AssetId,
}

export type AssetId = AdaAssetId | NativeTokenAssetId

export interface AdaAssetId {
  tag: "Ada",
}

export interface NativeTokenAssetId {
  tag: "NativeToken",
  policyId: string,
  assetName: string,
}

export interface Ratio {
  numerator: number,
  denominator: number,
}

export type Side = { tag: "Bid" } | { tag: "Ask" }

export type OrderStatus =
  | { tag: "New" }
  | { tag: "PartiallyFilled" }
  | { tag: "Filled" }
  | { tag: "Cancelled" }

export interface Destination {
  address: string,
  datum: Datum | null,
}

export type Datum = DatumVariantInline | DatumVariantHash

export interface DatumVariantInline {
  tag: "Inline",
  inline: string,
}

export interface DatumVariantHash {
  tag: "Hash",
  hash: string,
}

export function parseAssetValue(raw: any): AssetValue {
  return { asset: raw.asset, value: BigInt(raw.value) };
}

export function parseValue(raw: any): Value {
  return { assets: (raw?.assets ?? []).map(parseAssetValue) };
}

export function parseAdaAssetId(_raw: any): AdaAssetId {
  return { tag: "Ada" };
}

export function parseNativeTokenAssetId(raw: any): NativeTokenAssetId {
  return { tag: "NativeToken", policyId: raw.policyId, assetName: raw.assetName };
}

export function parseAssetId(raw: any): AssetId {
  return raw.tag === "Ada" ? parseAdaAssetId(raw) : parseNativeTokenAssetId(raw);
}

export function parseTradingPair(raw: any): TradingPair {
  return { base: parseAssetId(raw.base), quote: parseAssetId(raw.quote) };
}

export function parseRatio(raw: any): Ratio {
  return { numerator: raw.numerator, denominator: raw.denominator };
}

export function parseSide(raw: any): Side {
  return { tag: raw };
}

export function parseOrderStatus(raw: any): OrderStatus {
  return { tag: raw };
}

export function parseDatumVariantInline(raw: any): DatumVariantInline {
  return { tag: "Inline", inline: raw.inline };
}

export function parseDatumVariantHash(raw: any): DatumVariantHash {
  return { tag: "Hash", hash: raw.hash };
}

export function parseDatum(raw: any): Datum {
  return raw.tag === "Inline" ? parseDatumVariantInline(raw) : parseDatumVariantHash(raw);
}

export function parseDestination(raw: any): Destination {
  return { address: raw.address, datum: raw.datum ? parseDatum(raw.datum) : null };
}

export function parseAccount(raw: any): Account {
  return {
    ...raw,
    delegatedKey: raw.delegatedKey ?? null,
    nonce: BigInt(raw.nonce),
    uncommittedValue: parseValue(raw.uncommittedValue),
  };
}

export function parseDeposit(raw: any): Deposit {
  return {
    ...raw,
    requestId: BigInt(raw.requestId),
    absorbedInBlock: BigInt(raw.absorbedInBlock),
    rejectedInBlock: BigInt(raw.rejectedInBlock),
    value: parseValue(raw.value),
  };
}

export function parseMarket(raw: any): Market {
  return {
    key: raw.key,
    tradingPair: parseTradingPair(raw.tradingPair),
    takerFee: parseRatio(raw.takerFee),
    makerFee: parseRatio(raw.makerFee),
  };
}

export function parseOrderbookDepth(raw: any): OrderbookDepth {
  return {
    ...raw,
    bidAmount: BigInt(raw.bidAmount),
    askAmount: BigInt(raw.askAmount),
    price: BigInt(raw.price),
  };
}

export function parseOrder(raw: any): Order {
  return {
    ...raw,
    id: BigInt(raw.id),
    price: BigInt(raw.price),
    originalAmount: BigInt(raw.originalAmount),
    remaining: BigInt(raw.remaining),
    created: BigInt(raw.created),
    lastExecuted: BigInt(raw.lastExecuted),
    side: { tag: raw.side },
    status: { tag: raw.status },
  };
}

export function parseCandlestick(raw: any): Candlestick {
  return {
    ...raw,
    timestamp: BigInt(raw.timestamp),
    open: BigInt(raw.open),
    high: BigInt(raw.high),
    low: BigInt(raw.low),
    close: BigInt(raw.close),
    volume: BigInt(raw.volume),
  };
}

export function parseTpsStats(raw: any): TpsStats {
  return {
    ...raw,
    totalEvents: BigInt(raw.totalEvents),
    lastEventAt: BigInt(raw.lastEventAt),
    currentSecond: BigInt(raw.currentSecond),
  };
}
